/**
 * ContactDetail — the person page (PPL-265, contact-detail redesign).
 * Route: /contacts/:contactId
 *
 * Implements pplkpr-contact-detail-web.html: the phone's three panes become
 * one page — identity band across the top with every channel as a labelled
 * button, starters visible under it, the review queue pinned in the right
 * column, and only the two archives (timeline / the record) sharing the main
 * column.
 *
 * Where the canvas and the backend disagree, the backend wins:
 * - timeline rows REMOVE with a confirm-first dialog, no edit — the server's
 *   `undoWritebackItemEurW` is terminal (alias tombstoned) and no
 *   edit-timeline callable exists, so an "edit" affordance would be a lie.
 * - no "log a hangout": the timeline subtree is Admin-only and no callable
 *   writes user-authored entries yet.
 * - no "coming up / brief me": calendar is not built (PPL-273/274/277).
 * - starters are read-only + copy: generation and voting live in the app's
 *   queue/vote wiring, which web has never had — displaying what exists is
 *   real; wiring writes without that plumbing verified is not.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Check, Star, Loader, X, Copy, Plus, Edit2,
  Phone as PhoneIcon, Mail, MessageCircle,
} from 'react-feather';
import { useParams, useNavigate } from 'react-router-dom';
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, limit as fsLimit } from 'firebase/firestore';
import { FIRESTORE_DB } from '../firebaseConfig';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import SEO from '../components/SEO';
import MonoAvatar from '../components/MonoAvatar';
import {
  getContact,
  updateContact,
  deleteContact,
  toggleFavourite,
} from '../services/contactService';
import {
  IDENTITY_TYPES,
  channelLabelOf,
  editTimelineEntry,
  kindOf,
  logHangout,
  matchLabel,
  removeTimelineEntry,
  subscribePersonTimeline,
  type TimelineEntry,
} from '../services/writebackService';
import { useWriteback, useWritebackItems } from '../contexts/WritebackContext';
import {
  RELATIONSHIP_OPTIONS,
  FREQUENCY_OPTIONS,
  INTENT_OPTIONS,
  MEETING_CONTEXT_OPTIONS,
} from '../types/contacts';
import { ArchiveRow, RememberControl, RememberStatusPill, UpgradeSheet } from '../components/remember';
import useRememberPerson from '../hooks/useRememberPerson';
import { PaywallGate, PaywallSource } from '../services/monetizationEvents';
import type { Contact } from '../types/contacts';
import { formatTimestamp } from '../utils/dateUtils';

/* ── palette (site vars + the design's accents) ─────────────────────── */
const T = {
  border: 'var(--glass-border, rgba(255,255,255,0.08))',
  muted: 'var(--text-muted, rgba(232,220,250,0.7))',
  dim: 'var(--text-dim, rgba(220,200,240,0.5))',
  // Feather's `color` prop becomes the SVG `stroke` ATTRIBUTE, where var()
  // is invalid — icons need concrete values or they render invisible.
  iconDim: 'rgba(220,200,240,0.5)',
  magenta: '#f0abfc',
  magenta2: '#d946ef',
  violet: '#a78bfa',
  green: '#34d399',
  amber: '#fdba74',
  sky: '#38bdf8',
};
const KIND_TONE: Record<string, string> = {
  fact: T.violet, event: T.sky, date: T.magenta, commitment: T.amber, note: T.green, identity: T.magenta,
};

/** The design's LIFeelings palette — the client renders the wording; the
 *  server stores only the structured value. */
const FEELINGS: { key: string; colour: string }[] = [
  { key: 'great', colour: '#34d399' },
  { key: 'good', colour: '#86efac' },
  { key: 'fine', colour: '#a78bfa' },
  { key: 'off', colour: '#fdba74' },
  { key: 'rough', colour: '#f87171' },
];
const FEELING_COLOUR = Object.fromEntries(FEELINGS.map((f) => [f.key, f.colour]));

/* ── channels ───────────────────────────────────────────────────────── */
interface Channel {
  key: string;
  act: string;      // label on the chip
  value: string;    // the handle / number / address
  href: string;     // where it opens
  tone: 'green' | 'magenta';
}

const CHANNEL_HREF: Record<string, (v: string) => string> = {
  call: (v) => `tel:${v}`,
  sms: (v) => `sms:${v}`,
  whatsapp: (v) => `https://wa.me/${v.replace(/[^\d+]/g, '')}`,
  email: (v) => `mailto:${v}`,
  linkedin: (v) => `https://www.linkedin.com/in/${v}`,
  facebook: (v) => `https://www.facebook.com/${v}/`,
  instagram: (v) => `https://www.instagram.com/${v}/`,
  landline: (v) => `tel:${v}`,
  reddit: (v) => `https://reddit.com/user/${v}`,
  telegram: (v) => `https://t.me/${v}`,
  x: (v) => `https://x.com/${v}`,
};
const SUPPORTED_LABELS = ['mobile', 'email', 'linkedin', 'facebook', 'instagram', 'landline', 'reddit', 'telegram', 'x'];
/** The app-side `type` category each label belongs to (its docs are {type, value, label}). */
const CHANNEL_TYPE: Record<string, string> = {
  mobile: 'phone', landline: 'phone', email: 'personal',
  linkedin: 'social', facebook: 'social', instagram: 'social',
  reddit: 'social', telegram: 'social', x: 'social',
};

/** The app's fan-out rule: a mobile number becomes call + whatsapp + sms. */
function expandChannels(
  docs: { label: string; type?: string; value: string }[],
  fallback: { phone?: string; email?: string },
): Channel[] {
  const out: Channel[] = [];
  const push = (key: string, act: string, value: string) => {
    const href = CHANNEL_HREF[act];
    if (href) out.push({ key, act, value, href: href(value), tone: act === 'whatsapp' ? 'green' : 'magenta' });
  };
  docs.forEach((d, i) => {
    const label = d.label === 'main' && d.type ? d.type : d.label;
    if (label === 'mobile') {
      push(`${i}:call`, 'call', d.value);
      push(`${i}:wa`, 'whatsapp', d.value);
      push(`${i}:sms`, 'sms', d.value);
    } else {
      push(`${i}:${label}`, label, d.value);
    }
  });
  // The web contact form's own phone/email fields, when the subcollection
  // doesn't already carry them.
  if (fallback.phone && !out.some((c) => c.act === 'call')) {
    push('doc:call', 'call', fallback.phone);
    push('doc:wa', 'whatsapp', fallback.phone);
    push('doc:sms', 'sms', fallback.phone);
  }
  if (fallback.email && !out.some((c) => c.act === 'email')) {
    push('doc:email', 'email', fallback.email);
  }
  return out;
}

/* ── small bits ─────────────────────────────────────────────────────── */
function Rule({ label, right }: { label: string; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 11 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: T.dim }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: T.border }} />
      {right}
    </div>
  );
}

function KindPill({ kind }: { kind: string }) {
  const c = KIND_TONE[kind] || T.violet;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 999, background: `${c}1c`, border: `1px solid ${c}3a`,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: c,
    }}>{kind}</span>
  );
}

function toDate(ts: unknown): Date | null {
  if (!ts) return null;
  const t = ts as { toDate?: () => Date; seconds?: number };
  if (typeof t.toDate === 'function') return t.toDate();
  if (typeof t.seconds === 'number') return new Date(t.seconds * 1000);
  return null;
}

function provenanceOf(e: TimelineEntry): string {
  const channel = channelLabelOf(e.provenance?.channel);
  const captured = toDate(e.provenance?.capturedAt);
  if (!captured) return `from ${channel}`;
  const days = Math.floor((Date.now() - captured.getTime()) / 86400000);
  const when = days <= 0 ? 'today'
    : days === 1 ? 'yesterday'
      : days < 7 ? captured.toLocaleDateString(undefined, { weekday: 'long' }).toLowerCase()
        : captured.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }).toLowerCase();
  return `from ${channel}, ${when}`;
}

/* ── timeline row (hover actions: edit + confirm-first removal) ─────── */
function TimelineRow({ entry, personId, firstName }: { entry: TimelineEntry; personId: string; firstName: string }) {
  const [state, setState] = useState<'active' | 'confirm' | 'removing' | 'removed' | 'editing' | 'saving'>('active');
  const [removeError, setRemoveError] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [draft, setDraft] = useState(entry.title || entry.detail || '');
  // The subscription refreshes `entry` after a successful edit; until it
  // does, render the saved draft so the row doesn't flash back. The latch
  // CLEARS once the snapshot delivers the saved value — otherwise a later
  // remote edit (another tab, the app) would be masked forever.
  const [savedTitle, setSavedTitle] = useState<string | null>(null);
  useEffect(() => {
    if (savedTitle !== null && entry.title === savedTitle) setSavedTitle(null);
  }, [entry.title, savedTitle]);
  const kind = kindOf(entry.kind);
  const c = KIND_TONE[kind] || T.violet;

  const doRemove = async () => {
    setState('removing');
    setRemoveError(false);
    try {
      await removeTimelineEntry(personId, entry.entryId || entry.id);
      setState('removed');
    } catch {
      // A failed remove must not read as removed — or as nothing.
      setState('active');
      setRemoveError(true);
    }
  };

  const doEdit = async () => {
    const next = draft.trim();
    // Compare against the RENDERED title (savedTitle-aware): comparing the
    // raw entry would silently drop a revert typed before the snapshot
    // refresh, and re-save unchanged text in that same window.
    const current = (savedTitle ?? (entry.title || entry.detail) ?? '').trim();
    if (!next || next === current) {
      setState('active');
      return;
    }
    setState('saving');
    setEditError(null);
    try {
      await editTimelineEntry(personId, entry.entryId || entry.id, { title: next });
      setSavedTitle(next);
      setState('active');
    } catch (err) {
      const code = (err as { code?: string })?.code || '';
      setState('editing');
      setEditError(
        code.includes('failed-precondition')
          ? "this entry was just settled elsewhere — it can't be edited any more"
          : "couldn't save the edit — try again",
      );
    }
  };

  if (state === 'removed') {
    return (
      <div style={{ padding: '9px 14px', fontSize: 12.5, color: T.dim }}>
        removed from the timeline
      </div>
    );
  }

  if (state === 'confirm') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
        <span style={{ flex: 1, fontSize: 12.5, color: T.muted, lineHeight: 1.5 }}>
          remove this from {firstName ? `${firstName}'s` : 'their'} timeline? what it changed on the profile is undone — your own later edits are kept. this can&apos;t be undone.
        </span>
        <button onClick={doRemove} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '6px 12px', borderRadius: 9, background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: 12, fontWeight: 600 }}>remove</button>
        <button onClick={() => setState('active')} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '6px 12px', borderRadius: 9, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}>keep</button>
      </div>
    );
  }

  const title = savedTitle ?? (entry.title || entry.detail);

  if (state === 'editing' || state === 'saving') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.03)' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, marginTop: 12, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            autoFocus
            value={draft}
            disabled={state === 'saving'}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void doEdit();
              if (e.key === 'Escape') { setDraft(title || ''); setState('active'); setEditError(null); }
            }}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,0.06)', border: `1px solid ${T.magenta2}`, borderRadius: 8, padding: '6px 10px', outline: 'none' }}
          />
          {/* Provenance stays visible during the edit — the line keeps saying
              where it came from no matter what the words become. */}
          <div style={{ fontSize: 11.5, color: T.dim, marginTop: 4 }}>{provenanceOf(entry)}</div>
          {editError ? <div style={{ fontSize: 11.5, color: '#fca5a5', marginTop: 4 }}>{editError}</div> : null}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, paddingTop: 4 }}>
          {state === 'saving' ? (
            <Loader size={13} className="spin" style={{ color: T.dim }} />
          ) : (
            <>
              <button onClick={() => void doEdit()} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 10px', borderRadius: 8, background: 'var(--gradient-primary, linear-gradient(135deg,#d946ef,#8b5cf6))', border: 0, color: '#fff', fontSize: 11.5, fontWeight: 600 }}>save</button>
              <button onClick={() => { setDraft(title || ''); setState('active'); setEditError(null); }} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '4px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: T.muted, fontSize: 11.5 }}>cancel</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="wb-row" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderRadius: 12 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, marginTop: 7, flexShrink: 0, boxShadow: `0 0 10px ${c}80` }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: '#fff', letterSpacing: '-0.01em' }}>{title}</span>
          <KindPill kind={kind} />
          {entry.feeling && FEELING_COLOUR[entry.feeling] ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 999, background: `${FEELING_COLOUR[entry.feeling]}1c`, border: `1px solid ${FEELING_COLOUR[entry.feeling]}44`, fontSize: 10.5, color: '#fff' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: FEELING_COLOUR[entry.feeling] }} />
              felt {entry.feeling}
            </span>
          ) : null}
          {entry.editedByUser || savedTitle ? (
            <span style={{ fontSize: 10.5, color: T.dim }}>edited</span>
          ) : null}
        </div>
        {entry.detail && title && entry.detail !== title ? (
          <div style={{ fontSize: 12.5, color: T.muted, marginTop: 3, lineHeight: 1.5 }}>{entry.detail}</div>
        ) : null}
        <div style={{ fontSize: 11.5, color: T.dim, marginTop: 4 }}>{provenanceOf(entry)}</div>
        {removeError ? (
          <div style={{ fontSize: 11.5, color: '#fca5a5', marginTop: 4 }}>couldn&apos;t remove that — try again</div>
        ) : null}
      </div>
      <div className="wb-row-acts" style={{ flexShrink: 0, display: 'flex', gap: 4 }}>
        {state === 'removing' ? (
          <Loader size={13} className="spin" style={{ color: T.dim }} />
        ) : (
          <>
            <button onClick={() => { setDraft(title || ''); setState('editing'); }} title="edit" style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 8, background: 'transparent', border: '1px solid transparent', color: T.muted, fontSize: 11.5 }}>
              <Edit2 size={11} /> edit
            </button>
            <button onClick={() => setState('confirm')} title="remove from memory" style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 8, background: 'transparent', border: '1px solid transparent', color: T.muted, fontSize: 11.5 }}>
              <X size={11} /> remove
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ── manage channels (the canvas's "ways to reach her" sheet, with the
      add/remove the canvas only gestured at — web writes the same
      people/{pid}/contacts docs the app writes) ───────────────────────── */
function ChannelsModal({ uid, personId, firstName, docs, onClose }: {
  uid: string;
  personId: string;
  firstName: string;
  docs: { id: string; label: string; type?: string; value: string }[];
  onClose: () => void;
}) {
  const [newLabel, setNewLabel] = useState('mobile');
  const [newValue, setNewValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Delete mirrors the timeline's confirm-first pattern: one stray click on
  // an X must not silently drop a doc there is no undo for.
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const add = async () => {
    const value = newValue.trim();
    if (!value) return;
    setBusy(true);
    setError(null);
    try {
      // {type, value, label} is the shape EVERY other writer uses (app
      // ContactsAndSocialsScreen, saveContactsEurW) and the app renders
      // `type` unguarded — a doc without it crashes that screen. 'phone'
      // also keeps web-added numbers detectable by getContactChannels.
      await addDoc(collection(FIRESTORE_DB, 'users', uid, 'people', personId, 'contacts'), {
        label: newLabel,
        type: CHANNEL_TYPE[newLabel] || 'other',
        value,
      });
      setNewValue('');
    } catch {
      setError("couldn't add that channel — try again");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteDoc(doc(FIRESTORE_DB, 'users', uid, 'people', personId, 'contacts', id));
    } catch {
      setError("couldn't remove that channel — try again");
    }
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: 'inherit', fontSize: 13, color: '#fff',
    background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 10, padding: '9px 12px', outline: 'none',
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(6,2,10,0.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 480, maxWidth: '88%', maxHeight: '86vh', overflowY: 'auto', borderRadius: 20, background: 'rgba(24,13,34,0.98)', border: '1px solid rgba(255,255,255,0.16)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)', padding: '22px 24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h3 style={{ margin: 0, flex: 1, fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', color: '#fff' }}>
            ways to reach {firstName || 'them'}
          </h3>
          <button onClick={onClose} aria-label="close" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={15} color={T.iconDim} />
          </button>
        </div>

        {docs.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {docs.map((d) => {
              const actual = d.label === 'main' && d.type ? d.type : d.label;
              const isPhone = actual === 'mobile' || actual === 'landline';
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.035)', border: `1px solid ${T.border}` }}>
                  <span style={{ minWidth: 76, fontSize: 12.5, fontWeight: 600, color: '#fff', textTransform: 'capitalize' }}>{actual}</span>
                  {confirmId === d.id ? (
                    <>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#fde8b0' }}>
                        {isPhone ? 'remove this number? call, whatsapp and sms go with it — no undo.' : 'remove this channel? no undo.'}
                      </span>
                      <button onClick={() => { setConfirmId(null); void remove(d.id); }} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', border: '1px solid rgba(248,113,113,0.4)', background: 'rgba(248,113,113,0.12)', color: '#fca5a5', borderRadius: 8, padding: '4px 10px', fontSize: 11.5, fontWeight: 600 }}>remove</button>
                      <button onClick={() => setConfirmId(null)} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${T.border}`, background: 'transparent', color: T.muted, borderRadius: 8, padding: '4px 10px', fontSize: 11.5 }}>keep</button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.value}</span>
                      <button onClick={() => setConfirmId(d.id)} title="remove channel" style={{ appearance: 'none', cursor: 'pointer', border: 0, background: 'transparent', color: T.iconDim, display: 'inline-flex' }}>
                        <X size={13} color={T.iconDim} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: T.dim, marginBottom: 16 }}>no channels yet — add the first one below.</div>
        )}

        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: T.dim, marginBottom: 8 }}>add a channel</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={newLabel} onChange={(e) => setNewLabel(e.target.value)} style={{ ...inputStyle, width: 130, cursor: 'pointer' }}>
            {SUPPORTED_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
            placeholder={newLabel === 'mobile' || newLabel === 'landline' ? '+27 82 555 0134' : newLabel === 'email' ? 'name@example.com' : 'handle or username'}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={() => void add()} disabled={busy || !newValue.trim()} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, background: 'var(--gradient-primary, linear-gradient(135deg,#d946ef,#8b5cf6))', border: 0, color: '#fff', fontSize: 12.5, fontWeight: 600, opacity: busy || !newValue.trim() ? 0.5 : 1 }}>
            {busy ? <Loader size={12} className="spin" /> : <Plus size={12} />} add
          </button>
        </div>
        <div style={{ fontSize: 11, color: T.dim, marginTop: 10, lineHeight: 1.5 }}>
          a mobile number fans out into call, whatsapp and sms. changes show in the app too.
        </div>
        {error ? <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 10 }}>{error}</div> : null}
      </div>
    </div>
  );
}

/* ── log a hangout (A10 quick capture, desktop modal) ───────────────── */
function LogHangoutModal({ personId, firstName, onClose }: {
  personId: string;
  firstName: string;
  onClose: () => void;
}) {
  const [activity, setActivity] = useState('');
  const [feeling, setFeeling] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'saving' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setState('saving');
    setError(null);
    try {
      // Free text is the summary; the first chip word doubles as the kind so
      // "dinner at Brat" files under kind "dinner".
      const kind = ['coffee', 'dinner', 'a call', 'a walk'].find((q) => activity.toLowerCase().startsWith(q)) || 'hangout';
      await logHangout({
        personId,
        kind,
        summary: activity.trim() || undefined,
        feeling: feeling || undefined,
      });
      setState('done');
    } catch (err) {
      const code = (err as { code?: string })?.code || '';
      setState('idle');
      setError(
        code.includes('resource-exhausted')
          ? 'daily limit reached — 30 logged hangouts a day'
          : "couldn't save that — try again",
      );
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(6,2,10,0.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: '88%', maxHeight: '86vh', overflowY: 'auto', borderRadius: 20, background: 'rgba(24,13,34,0.98)', border: '1px solid rgba(255,255,255,0.16)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)', padding: '22px 24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <h3 style={{ margin: 0, flex: 1, fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', color: '#fff' }}>log a hangout</h3>
          <button onClick={onClose} aria-label="close" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={15} color={T.iconDim} />
          </button>
        </div>

        {state === 'done' ? (
          <div style={{ padding: '14px 0 6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(52,211,153,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Check size={14} color={T.green} strokeWidth={3} />
              </span>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
                logged — it&apos;s on {firstName ? `${firstName}'s` : 'the'} timeline
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onClose} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '8px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: T.muted, fontSize: 13 }}>done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 14 }}>
              what you did with {firstName || 'them'} — straight to the timeline, no review needed: you wrote it.
            </div>

            <input
              autoFocus
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              placeholder="dinner, a walk, a long call…"
              style={{ width: '100%', fontFamily: 'inherit', fontSize: 14, color: '#fff', background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 12, padding: '11px 14px', outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
              {['coffee', 'dinner', 'a call', 'a walk'].map((q) => (
                <button key={q} onClick={() => setActivity(q)} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '6px 12px', borderRadius: 999, background: 'transparent', border: `1px solid ${T.border}`, color: T.muted, fontSize: 12.5 }}>{q}</button>
              ))}
            </div>

            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase', color: T.dim, margin: '16px 0 8px' }}>
              how did it feel? <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>optional</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FEELINGS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFeeling(feeling === f.key ? null : f.key)}
                  style={{
                    appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 999,
                    background: feeling === f.key ? `${f.colour}22` : 'transparent',
                    border: `1px solid ${feeling === f.key ? `${f.colour}66` : T.border}`,
                    color: feeling === f.key ? '#fff' : T.muted, fontSize: 12.5, fontWeight: feeling === f.key ? 600 : 500,
                  }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: f.colour }} />{f.key}
                </button>
              ))}
            </div>

            {error ? <div style={{ fontSize: 12, color: '#fca5a5', marginTop: 12 }}>{error}</div> : null}

            <div style={{ display: 'flex', gap: 9, marginTop: 18, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '8px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: T.muted, fontSize: 13 }}>cancel</button>
              <button onClick={() => void save()} disabled={state === 'saving'} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, background: 'var(--gradient-primary, linear-gradient(135deg,#d946ef,#8b5cf6))', border: 0, color: '#fff', fontSize: 13, fontWeight: 600, opacity: state === 'saving' ? 0.6 : 1 }}>
                {state === 'saving' ? <Loader size={12} className="spin" /> : <Plus size={12} />} save
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── the page ───────────────────────────────────────────────────────── */
export default function ContactDetail() {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();
  const uid = user?.uid;

  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteStep, setDeleteStep] = useState<'idle' | 'confirm' | 'deleting'>('idle');

  const [tab, setTab] = useState<'timeline' | 'record'>('timeline');
  const [channelDocs, setChannelDocs] = useState<{ id: string; label: string; type?: string; value: string }[]>([]);
  const [timeline, setTimeline] = useState<TimelineEntry[] | null>(null);
  const [starters, setStarters] = useState<{ id: string; text: string }[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1200);
  const [hangoutOpen, setHangoutOpen] = useState(false);
  const [channelsOpen, setChannelsOpen] = useState(false);

  // PPL-350 — f8's memory of THIS person: whether it is keeping up with them, the
  // account's remaining slots, and how their memories split across the window
  // edge. Subscribed rather than read, because the writes are server-side.
  const remember = useRememberPerson(uid, contactId || null, {
    // The contact is already loaded, so the control renders its real state on the
    // first paint instead of flashing "f8 doesn't remember them yet" at someone
    // it has remembered for months.
    seed: contact,
    onError: (message) => setError(message || null),
  });

  /**
   * "restore" on the archive row.
   *
   * The row appears for anyone with dormant memories, including ppl f8 is not
   * currently keeping up with — so sending every click to the upgrade sheet would
   * tell a free user with two slots spare that "every slot is full". Only an
   * ACTIVE person's archive genuinely needs premium: theirs is dormant because of
   * the window, and nothing but a plan change moves that line.
   */
  const onRestoreArchive = () => {
    // An ACTIVE person's archive is dormant because of the WINDOW, not because
    // the slots are gone — so it gets its own refusal. Saying "every slot is
    // full" to someone with two free is untrue and sells the wrong thing.
    if (remember.state === 'active') { remember.showLimit('window'); return; }
    // Unknown quota defaults to "there is room": attempting the write costs a
    // round trip and the server refuses authoritatively if there is not, whereas
    // guessing "full" refuses someone who may well have slots to spare.
    const room = remember.quota === null
      || remember.quota.remaining === null
      || remember.quota.remaining > 0;
    if (room) remember.remember();
    else remember.showLimit();
  };

  const { resolved, resolve, dismiss, undo, undoDismiss, clearResolved } = useWriteback();
  const reviewItems = useWritebackItems(contactId || null);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<Partial<Contact> | null>(null);

  useEffect(() => {
    if (!isAuthenticated && !loading) redirectToLogin();
  }, [isAuthenticated, loading, redirectToLogin]);

  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 1200);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!uid || !contactId) return undefined;
    let stale = false; // a slow response for the PREVIOUS contact must not win
    setLoading(true);
    setTab('timeline');
    setDeleteStep('idle');
    // Modals must not survive navigating to another contact — an open sheet
    // with a typed value would write it to the NEW person's subtree.
    setChannelsOpen(false);
    setHangoutOpen(false);
    (async () => {
      try {
        const c = await getContact(uid, contactId);
        if (stale) return;
        if (!c) {
          navigate('/contacts', { replace: true });
          return;
        }
        setContact(c);
      } catch {
        if (!stale) setError('Failed to load contact');
      } finally {
        if (!stale) setLoading(false);
      }
    })();
    return () => {
      stale = true;
      // Debounced edits belong to THIS contact. Navigating away inside the
      // 1s window used to drop the edit AND leave its fields in pendingRef,
      // where the next contact's save would write them into the WRONG doc.
      // Flush to the closure's (old) contactId, then clear.
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      if (pendingRef.current) {
        void updateContact(uid, contactId, pendingRef.current as any).catch(() => {});
        pendingRef.current = null;
      }
    };
  }, [uid, contactId, navigate]);

  // channels — the app writes these to people/{pid}/contacts; owner-readable.
  // Reset FIRST: on a param-only navigation between two contacts, the old
  // person's chips must not linger while the new subscription warms up.
  useEffect(() => {
    setChannelDocs([]);
    if (!uid || !contactId) return undefined;
    return onSnapshot(
      collection(FIRESTORE_DB, 'users', uid, 'people', contactId, 'contacts'),
      (snap) => {
        const rows: { id: string; label: string; type?: string; value: string }[] = [];
        snap.forEach((d) => {
          const data = d.data();
          if (!data?.label || !data?.value) return;
          const actual = data.label === 'main' && data.type ? data.type : data.label;
          if (SUPPORTED_LABELS.includes(actual)) rows.push({ id: d.id, label: data.label, type: data.type, value: data.value });
        });
        setChannelDocs(rows);
      },
      () => setChannelDocs([]),
    );
  }, [uid, contactId]);

  // timeline — what f8 knows (reset to loading on person change)
  useEffect(() => {
    setTimeline(null);
    if (!uid || !contactId) return undefined;
    return subscribePersonTimeline(uid, contactId, setTimeline, () => setTimeline([]));
  }, [uid, contactId]);

  // conversation starters — read-only surface of the app's messages data
  useEffect(() => {
    setStarters([]);
    if (!uid || !contactId) return undefined;
    return onSnapshot(
      query(
        collection(FIRESTORE_DB, 'users', uid, 'people', contactId, 'messages'),
        orderBy('createdDate', 'desc'),
        fsLimit(3),
      ),
      (snap) => {
        const rows: { id: string; text: string }[] = [];
        snap.forEach((d) => {
          const text = d.data()?.text;
          if (typeof text === 'string' && text) rows.push({ id: d.id, text });
        });
        setStarters(rows);
      },
      () => setStarters([]),
    );
  }, [uid, contactId]);

  // The review overlay is per-visit — drop only this page's ids on the way out.
  const shownRef = useRef<string[]>([]);
  shownRef.current = reviewItems.map((i) => i.id);
  useEffect(() => () => clearResolved(shownRef.current), [clearResolved]);

  const saveField = useCallback(
    (partial: Partial<Contact>) => {
      if (!uid || !contactId) return;
      pendingRef.current = { ...(pendingRef.current || {}), ...partial };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        if (!pendingRef.current) return;
        setSaving(true);
        try {
          await updateContact(uid, contactId, pendingRef.current as any);
          pendingRef.current = null;
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        } catch {
          setError('Failed to save');
        } finally {
          setSaving(false);
        }
      }, 1000);
    },
    [uid, contactId],
  );

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const updateField = (key: keyof Contact, value: any) => {
    if (!contact) return;
    setContact({ ...contact, [key]: value });
    saveField({ [key]: value });
  };

  const handleFav = async () => {
    if (!uid || !contactId || !contact) return;
    const newVal = !contact.isFavourite;
    setContact({ ...contact, isFavourite: newVal });
    try {
      await toggleFavourite(uid, contactId, contact.isFavourite);
    } catch {
      setContact({ ...contact, isFavourite: !newVal });
    }
  };

  const handleDelete = async () => {
    if (!uid || !contactId) return;
    setDeleteStep('deleting');
    try {
      await deleteContact(uid, contactId);
      navigate('/contacts', { replace: true });
    } catch {
      setError('Failed to delete contact');
      setDeleteStep('confirm');
    }
  };

  const copyStarter = async (id: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* clipboard unavailable */ }
  };

  if (!isAuthenticated) return null;

  const relGroups = RELATIONSHIP_OPTIONS.reduce<Record<string, typeof RELATIONSHIP_OPTIONS>>((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {});

  const contactName = contact ? `${contact.firstName} ${contact.lastName}`.trim() : '';
  const firstName = contact?.nickname || contact?.firstName || '';
  const channels = contact ? expandChannels(channelDocs, { phone: contact.phone, email: contact.email }) : [];
  const knows = timeline?.length ?? 0;
  const hangouts = (timeline || []).filter((e) => e.kind === 'log_interaction').length;
  const pendingHere = reviewItems.filter((i) => !resolved[i.id]).length;

  // Profile completeness — a fact, not a scold.
  const completeness = contact ? (() => {
    const checks = [
      !!contact.firstName, !!(contact.lastName || contact.nickname), !!contact.relationship,
      !!contact.dateOfBirth, !!contact.phone, !!contact.email,
      !!contact.meetingContext, !!contact.frequency, !!contact.intent, !!contact.notes,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  })() : 0;

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.border}`,
    color: '#fff', fontSize: 13.5, fontFamily: 'inherit', outline: 'none',
  };
  const selectStyle: React.CSSProperties = {
    ...inputStyle, appearance: 'none' as const, cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: 32,
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-dim)',
    marginBottom: 6, textTransform: 'lowercase' as const,
  };
  const fieldRowStyle: React.CSSProperties = { marginBottom: 14 };

  return (
    <div className="feed">
      <SEO
        title={contact ? `${contactName} | pplkpr` : 'Contact | pplkpr'}
        description="View and edit contact details."
        path={`/contacts/${contactId}`}
      />
      <style>{`.wb-row:hover{background:rgba(255,255,255,0.04)} .wb-row .wb-row-acts{opacity:0} .wb-row:hover .wb-row-acts{opacity:1}`}</style>

      <div className="page-shell">
        <div className="page-shell__inner" style={{ maxWidth: 1180 }}>
          {/* Top bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <button
              onClick={() => navigate('/contacts')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999,
                background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.border}`, color: 'var(--text-muted)',
                cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 500,
              }}
            >
              <ArrowLeft size={14} /> all people
            </button>
            <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              {saving && <span>saving...</span>}
              {saved && (
                <span style={{ color: '#4CAF50', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Check size={14} /> saved
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
              <Loader size={20} className="spin" style={{ color: 'var(--text-dim)' }} />
            </div>
          ) : error && !contact ? (
            <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.2)', color: '#ff6b6b', fontSize: 13 }}>
              {error}
            </div>
          ) : contact ? (
            <>
              {error && (
                <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.2)', color: '#ff6b6b', fontSize: 13, marginBottom: 16 }}>
                  {error}
                </div>
              )}

              {/* ── identity band ─────────────────────────────────────── */}
              <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <MonoAvatar name={contactName || '?'} size={64} />
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
                        {contactName}
                      </h2>
                      {contact.nickname && (
                        <span style={{ color: 'var(--text-dim)', fontSize: 14 }}>({contact.nickname})</span>
                      )}
                      {contact.relationship && (
                        <span className="tag-pill tag-pill--violet tag-pill--sm">
                          {RELATIONSHIP_OPTIONS.find((r) => r.value === contact.relationship)?.label}
                        </span>
                      )}
                      {contact.lastContactDate ? (
                        <span style={{ fontSize: 12, color: T.dim }}>last contact {formatTimestamp(contact.lastContactDate)}</span>
                      ) : null}
                      {/* PPL-350 — "f8 remembers" / "paused". Renders NOTHING for
                          a person f8 was never asked to remember, which is most of
                          a synced address book: the absence of a chip is the third
                          state, and labelling it would make the chip meaningless. */}
                      <RememberStatusPill state={remember.state} />
                    </div>

                    {/* channels — every way to reach them, value visible */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                      {channels.map((ch) => (
                        <a
                          key={ch.key}
                          href={ch.href}
                          target={ch.href.startsWith('http') ? '_blank' : undefined}
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 999,
                            border: `1px solid ${ch.tone === 'green' ? 'rgba(52,211,153,0.3)' : T.border}`,
                            background: ch.tone === 'green' ? 'rgba(52,211,153,0.08)' : 'rgba(255,255,255,0.045)',
                            color: '#fff', fontSize: 12.5, fontWeight: 600, textDecoration: 'none',
                          }}
                        >
                          {ch.act === 'call' || ch.act === 'landline' ? <PhoneIcon size={12} />
                            : ch.act === 'email' ? <Mail size={12} />
                              : <MessageCircle size={12} />}
                          {ch.act}
                          <span style={{ fontSize: 11.5, fontWeight: 500, color: T.dim }}>{ch.value}</span>
                        </a>
                      ))}
                      <button
                        onClick={() => setChannelsOpen(true)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
                          border: '1px dashed rgba(255,255,255,0.16)', background: 'transparent',
                          color: T.muted, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        <Plus size={12} /> add a channel
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => navigate('/f8chat', { state: { initialMessage: `I want to chat about my relationship with ${firstName || contactName}` } })}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 10,
                        background: 'var(--gradient-primary, linear-gradient(135deg,#d946ef,#8b5cf6))',
                        border: '1px solid transparent', color: '#fff', fontSize: 12.5, fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      ✦ ask f8 about {firstName || 'them'}
                    </button>
                    <button
                      onClick={handleFav}
                      title={contact.isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10,
                        background: contact.isFavourite ? 'rgba(253,224,71,0.15)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${contact.isFavourite ? 'rgba(253,224,71,0.3)' : T.border}`,
                        color: contact.isFavourite ? '#fde047' : 'var(--text-dim)', cursor: 'pointer',
                      }}
                    >
                      <Star size={17} fill={contact.isFavourite ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                </div>
              </div>

              {/* PPL-350 — the primary control. Directly under the identity
                  band because for a never-remembered contact it IS this page's
                  main action, and the line under it is where ppl learn what
                  remembering means. */}
              <RememberControl
                firstName={firstName || contactName}
                state={remember.state}
                quota={remember.quota}
                busy={remember.busy}
                onRemember={remember.remember}
                onPause={remember.pause}
              />

              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                {/* ── main column ─────────────────────────────────────── */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* starters — read-only surface of what f8 wrote in the app */}
                  {starters.length > 0 ? (
                    <div style={{ marginBottom: 20 }}>
                      <Rule label="conversation starters" />
                      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
                        {starters.map((st) => (
                          <div key={st.id} style={{
                            flex: '1 1 240px', minWidth: 220, display: 'flex', flexDirection: 'column',
                            borderRadius: 16, background: 'linear-gradient(180deg, rgba(60,30,90,0.36), rgba(30,15,50,0.44))',
                            border: `1px solid ${T.border}`,
                          }}>
                            <div style={{ padding: '13px 14px 0', flex: 1 }}>
                              <div style={{ fontSize: 13, color: '#f3e8ff', lineHeight: 1.5 }}>{st.text}</div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 10px' }}>
                              <button
                                onClick={() => copyStarter(st.id, st.text)}
                                style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: copiedId === st.id ? T.green : T.muted, fontSize: 11.5, fontWeight: 600 }}
                              >
                                <Copy size={11} /> {copiedId === st.id ? 'copied' : 'copy'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* tab switch — the two archives share the main column */}
                  <div style={{ display: 'inline-flex', gap: 3, padding: 3, borderRadius: 11, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, marginBottom: 16 }}>
                    {([['timeline', `timeline${knows ? ` · ${knows}` : ''}`], ['record', 'the record']] as const).map(([id, label]) => {
                      const on = tab === id;
                      return (
                        <button key={id} onClick={() => setTab(id)} style={{
                          appearance: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit', padding: '7px 18px', borderRadius: 8,
                          background: on ? 'var(--gradient-primary, linear-gradient(135deg,#d946ef,#8b5cf6))' : 'transparent',
                          color: on ? '#fff' : T.muted, fontSize: 12.5, fontWeight: on ? 700 : 500,
                        }}>{label}</button>
                      );
                    })}
                  </div>

                  {tab === 'timeline' ? (
                    <div>
                      <Rule
                        label={
                          // PPL-350 — which side of the window these sit on, for
                          // someone f8 is actively keeping up with and only when a
                          // window is in force. For a paused person "0 kept" is a
                          // strange way to say "f8 stopped keeping up", which the
                          // control above already says properly; with no window
                          // there is no line to be on either side of.
                          remember.state === 'active'
                          && remember.archive
                          && remember.archive.memoryWindowDays !== null
                          && remember.archive.kept !== null
                          && remember.archive.archived !== null
                            ? `what f8 knows · ${remember.archive.kept} kept · ${remember.archive.archived} archived`
                            : `what f8 knows${knows ? ` · ${knows} thing${knows === 1 ? '' : 's'}` : ''}`
                        }
                        right={
                          <button onClick={() => setHangoutOpen(true)} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 9, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: 'rgba(240,232,250,0.88)', fontSize: 11.5, fontWeight: 600 }}>
                            <Plus size={11} /> log a hangout
                          </button>
                        }
                      />
                      {timeline === null ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
                          <Loader size={16} className="spin" style={{ color: 'var(--text-dim)' }} />
                        </div>
                      ) : timeline.length === 0 ? (
                        <div className="glass-panel" style={{ padding: 18 }}>
                          <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
                            nothing here yet. accept something f8 noticed about {firstName || 'them'} on the{' '}
                            <a href="/insights" onClick={(e) => { e.preventDefault(); navigate('/insights'); }}>insights page</a>, or log a hangout above — both land on this timeline.
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="glass-panel" style={{ padding: '6px 4px' }}>
                            {timeline.map((e) => (
                              <TimelineRow key={e.entryId || e.id} entry={e} personId={contactId as string} firstName={firstName} />
                            ))}
                          </div>
                          {/* PPL-377 — what f8 holds but is not using. Renders
                              nothing when no window is in force (premium, or the
                              fence still off server-side) or when the count could
                              not be established: a row reading "0 memories
                              archived" to someone with eighty is worse than no
                              row, and while the fence is off those memories are
                              not dormant at all. */}
                          <ArchiveRow
                            archived={remember.archive?.archived ?? null}
                            memoryWindowDays={remember.archive?.memoryWindowDays ?? null}
                            paused={remember.archive?.paused ?? false}
                            firstName={firstName || contactName}
                            onRestore={onRestoreArchive}
                          />
                          <div style={{ fontSize: 11.5, color: T.dim, marginTop: 10, lineHeight: 1.6 }}>
                            every line names where it came from — and anything f8 saved on its own can be undone.
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div>
                      {/* the record — web's editable form, in place */}
                      <div className="glass-panel" style={{ marginBottom: 16, padding: 20 }}>
                        <Rule label="basic info" />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                          <div style={fieldRowStyle}>
                            <label style={labelStyle}>first name</label>
                            <input type="text" value={contact.firstName} onChange={(e) => updateField('firstName', e.target.value)} style={inputStyle} />
                          </div>
                          <div style={fieldRowStyle}>
                            <label style={labelStyle}>last name</label>
                            <input type="text" value={contact.lastName} onChange={(e) => updateField('lastName', e.target.value)} style={inputStyle} />
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                          <div style={fieldRowStyle}>
                            <label style={labelStyle}>nickname</label>
                            <input type="text" value={contact.nickname || ''} onChange={(e) => updateField('nickname', e.target.value)} placeholder="optional nickname" style={inputStyle} />
                          </div>
                          <div style={fieldRowStyle}>
                            <label style={labelStyle}>date of birth</label>
                            <input type="date" value={contact.dateOfBirth || ''} onChange={(e) => updateField('dateOfBirth', e.target.value)} style={inputStyle} />
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                          <div style={fieldRowStyle}>
                            <label style={labelStyle}>phone</label>
                            <input type="tel" value={contact.phone || ''} onChange={(e) => updateField('phone', e.target.value)} placeholder="phone number" style={inputStyle} />
                          </div>
                          <div style={fieldRowStyle}>
                            <label style={labelStyle}>email</label>
                            <input type="email" value={contact.email || ''} onChange={(e) => updateField('email', e.target.value)} placeholder="email address" style={inputStyle} />
                          </div>
                        </div>
                      </div>

                      <div className="glass-panel" style={{ marginBottom: 16, padding: 20 }}>
                        <Rule label="relationship" />
                        <div style={fieldRowStyle}>
                          <label style={labelStyle}>relationship</label>
                          <select value={contact.relationship} onChange={(e) => updateField('relationship', e.target.value)} style={selectStyle}>
                            <option value="">select...</option>
                            {Object.entries(relGroups).map(([group, opts]) => (
                              <optgroup key={group} label={group}>
                                {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </optgroup>
                            ))}
                          </select>
                        </div>
                        <div style={fieldRowStyle}>
                          <label style={labelStyle}>how you met</label>
                          <select value={contact.meetingContext || ''} onChange={(e) => updateField('meetingContext', e.target.value)} style={selectStyle}>
                            <option value="">select...</option>
                            {MEETING_CONTEXT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        <div style={fieldRowStyle}>
                          <label style={labelStyle}>notes</label>
                          <textarea value={contact.notes || ''} onChange={(e) => updateField('notes', e.target.value)} placeholder="personal notes about this person..." rows={3} style={{ ...inputStyle, resize: 'vertical' as const }} />
                        </div>
                      </div>

                      <div className="glass-panel" style={{ marginBottom: 16, padding: 20 }}>
                        <Rule label="engagement" />
                        <div style={fieldRowStyle}>
                          <label style={labelStyle}>contact frequency</label>
                          <select value={contact.frequency || ''} onChange={(e) => updateField('frequency', e.target.value)} style={selectStyle}>
                            <option value="">select...</option>
                            {FREQUENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label} — {o.desc}</option>)}
                          </select>
                        </div>
                        <div style={fieldRowStyle}>
                          <label style={labelStyle}>intent</label>
                          <select value={contact.intent || ''} onChange={(e) => updateField('intent', e.target.value)} style={selectStyle}>
                            <option value="">select...</option>
                            {INTENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        {contact.connectionScore != null && (
                          <div style={fieldRowStyle}>
                            <label style={labelStyle}>connection score</label>
                            <div style={{ position: 'relative', height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${contact.connectionScore}%`, borderRadius: 4, background: 'linear-gradient(135deg, #d946ef, #8b5cf6)' }} />
                            </div>
                            <span style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4, display: 'block' }}>{contact.connectionScore}%</span>
                          </div>
                        )}
                      </div>

                      {/* danger zone — states its consequences */}
                      <div className="glass-panel" style={{ padding: 20, marginBottom: 32, borderColor: 'rgba(255,107,107,0.15)' }}>
                        <Rule label="danger zone" />
                        {deleteStep === 'idle' && (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                            <span style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                              delete {firstName || 'this person'}
                              {knows ? ` — removes the profile and the ${knows} thing${knows === 1 ? '' : 's'} f8 knows` : ''}
                            </span>
                            <button onClick={() => setDeleteStep('confirm')} style={{ padding: '8px 14px', borderRadius: 10, background: 'rgba(255,107,107,0.12)', border: '1px solid rgba(255,107,107,0.2)', color: '#ff6b6b', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 500, flexShrink: 0 }}>
                              delete contact
                            </button>
                          </div>
                        )}
                        {deleteStep === 'confirm' && (
                          <div>
                            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.55 }}>
                              Delete <strong style={{ color: '#fff' }}>{contactName}</strong>? This removes the profile
                              {knows ? `, the ${knows} thing${knows === 1 ? '' : 's'} f8 knows` : ''}
                              {starters.length ? `, ${starters.length} conversation starter${starters.length === 1 ? '' : 's'}` : ''}
                              {' '}and every logged detail. It cannot be undone.
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => setDeleteStep('idle')} style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: `1px solid ${T.border}`, color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                                keep {firstName || 'them'}
                              </button>
                              <button onClick={handleDelete} style={{ padding: '8px 16px', borderRadius: 10, background: '#ff6b6b', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 600 }}>
                                yes, delete
                              </button>
                            </div>
                          </div>
                        )}
                        {deleteStep === 'deleting' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-dim)', fontSize: 13 }}>
                            <Loader size={14} className="spin" /><span>deleting...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── right sidebar: what to do now ───────────────────── */}
                {wide ? (
                  <aside style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {/* to review — the same queue as /insights, pre-filtered */}
                    {reviewItems.length > 0 ? (
                      <div>
                        <Rule
                          label="to review"
                          right={pendingHere > 0 ? (
                            <span style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: 'rgba(217,70,239,0.18)', border: '1px solid rgba(217,70,239,0.34)', fontSize: 10, fontWeight: 700, color: T.magenta, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{pendingHere}</span>
                          ) : undefined}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {reviewItems.map((item) => {
                            const state = resolved[item.id];
                            if (state) {
                              const good = state.status === 'accepted';
                              const undone = state.status === 'undone';
                              const conflicted = state.status === 'conflicted';
                              // A score belongs only on a receipt f8 wrote
                              // itself — on an accept, the user decided.
                              const score = state.autoSaved && item.confidencePct !== null
                                ? ` · ${item.confidencePct}%` : '';
                              // "not john?" is the wrong-PERSON answer: it
                              // reverts and tombstones the alias. A
                              // link_mention's plain undo already does that.
                              const offerWrongPerson = !!state.autoSaved && !!item.personId
                                && !IDENTITY_TYPES.includes(item.type) && !!firstName;
                              return (
                                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', borderRadius: 11, background: good ? 'rgba(52,211,153,0.08)' : conflicted ? 'rgba(251,191,36,0.07)' : 'rgba(255,255,255,0.025)', border: `1px solid ${good ? 'rgba(52,211,153,0.24)' : conflicted ? 'rgba(251,191,36,0.24)' : T.border}` }}>
                                  {good ? <Check size={12} color={T.green} /> : <X size={11} color={T.iconDim} />}
                                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: good ? 'rgba(240,232,250,0.85)' : conflicted ? '#fde8b0' : T.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {good ? `saved to ${firstName || 'the timeline'}${score}`
                                      : conflicted ? 'your version was kept'
                                        : undone ? 'not saved' : 'dismissed'}
                                  </span>
                                  {good && !state.pendingCall && offerWrongPerson ? (
                                    <button onClick={() => { void undo(item.id, 'wrong_person'); }} title="wrong person — f8 won't link this name to them again" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: T.muted, whiteSpace: 'nowrap' }}>not {firstName}?</button>
                                  ) : null}
                                  {good && !state.pendingCall ? (
                                    <button onClick={() => { void undo(item.id); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, color: T.magenta }}>undo</button>
                                  ) : null}
                                  {state.status === 'dismissed' && state.undoable ? (
                                    <button onClick={() => undoDismiss(item.id)} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, color: T.magenta }}>undo</button>
                                  ) : null}
                                </div>
                              );
                            }
                            const isChooser = item.kind === 'identity' && item.candidates.length > 0;
                            return (
                              <div key={item.id} style={{ padding: '12px 13px', borderRadius: 13, background: 'rgba(255,255,255,0.035)', border: `1px solid ${T.border}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                                  <KindPill kind={item.kind} />
                                  <span style={{ fontSize: 11, color: T.dim }}>{item.field}</span>
                                  {item.personId && item.confidencePct !== null ? (
                                    <span style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, fontVariantNumeric: 'tabular-nums' }}>
                                      {matchLabel(firstName || contactName, item.confidenceBand, item.confidencePct)}
                                    </span>
                                  ) : null}
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.45, color: '#fff' }}>{item.text}</div>
                                <div style={{ fontSize: 11, color: T.dim, marginTop: 7 }}>{item.src} · waiting for you</div>
                                <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
                                  {isChooser ? (
                                    // "which Sarah?" needs the chooser — that lives on /insights.
                                    <button onClick={() => navigate('/insights')} style={{ flex: 1, appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '6px 10px', borderRadius: 9, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: T.muted, fontSize: 11.5, fontWeight: 600 }}>
                                      choose who on insights →
                                    </button>
                                  ) : (
                                    <>
                                      <button onClick={() => { void resolve(item.id, 'accept'); }} style={{ flex: 1, appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 10px', borderRadius: 9, background: 'var(--gradient-primary, linear-gradient(135deg,#d946ef,#8b5cf6))', border: '1px solid transparent', color: '#fff', fontSize: 11.5, fontWeight: 600 }}>
                                        <Check size={11} /> save
                                      </button>
                                      <button onClick={() => dismiss(item.id)} style={{ appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '6px 10px', borderRadius: 9, background: 'rgba(255,255,255,0.05)', border: `1px solid ${T.border}`, color: T.muted, fontSize: 11.5, fontWeight: 600 }}>no</button>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                    {/* completeness ring */}
                    <div>
                      <Rule label="how complete this profile is" />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                        <span style={{ position: 'relative', width: 46, height: 46, flexShrink: 0 }}>
                          <svg width="46" height="46" viewBox="0 0 46 46">
                            <circle cx="23" cy="23" r="19" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
                            <circle cx="23" cy="23" r="19" fill="none" stroke="#e879f9" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${(completeness / 100) * 119.4} 119.4`} transform="rotate(-90 23 23)" />
                          </svg>
                          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, color: '#fff' }}>{completeness}%</span>
                        </span>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
                          the record tab fills the gaps — everything saves as you type.
                        </div>
                      </div>
                    </div>

                    <div style={{ height: 1, background: T.border }} />

                    {/* engagement */}
                    <div>
                      <Rule label="engagement" />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {([[String(knows), 'things f8 knows'], [String(hangouts), 'hangouts logged'], [String(starters.length), 'starters written']] as const)
                          .filter(([n]) => n !== '0')
                          .map(([n, l]) => (
                            <div key={l} style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
                              <span style={{ fontSize: 15, fontWeight: 700, color: T.magenta, letterSpacing: '-0.02em', minWidth: 40 }}>{n}</span>
                              <span style={{ fontSize: 12, color: T.muted }}>{l}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    <div style={{ height: 1, background: T.border }} />
                    <div style={{ fontSize: 11.5, color: T.dim, lineHeight: 1.6 }}>
                      f8 writes into this profile only when it&apos;s sure who it&apos;s about, and tells you when it does. anything less waits in{' '}
                      <span style={{ color: T.magenta }}>to review</span> — and every save can be undone.
                    </div>
                  </aside>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* PPL-350 — the at-limit moment. Opened ONLY by the server's
          `remembered_people_limit` refusal or by an explicit click on a full
          counter, never by any other failure: a network blip must not be sold to.
          `gate_hit` is emitted server-side by the callable, so nothing fires it
          here — doing both would double-count the tier's key funnel number. */}
      <UpgradeSheet
        open={remember.limitPrompt.open}
        mode={remember.limitPrompt.mode}
        firstName={firstName || contactName}
        quota={remember.quota}
        archive={remember.accountArchive}
        onUpgrade={() => {
          remember.dismissLimit();
          // Carries the origin through, so the premium page's own
          // `paywall_viewed` is attributable to the gate that sent them rather
          // than looking like a direct visit (PPL-351).
          navigate(`/premium?source=${PaywallSource.REMEMBERED_LIMIT_SHEET}`
            + `&gate=${PaywallGate.REMEMBERED_PEOPLE}`);
        }}
        onClose={remember.dismissLimit}
      />

      {hangoutOpen && contactId ? (
        <LogHangoutModal personId={contactId} firstName={firstName} onClose={() => setHangoutOpen(false)} />
      ) : null}
      {channelsOpen && contactId && uid ? (
        <ChannelsModal uid={uid} personId={contactId} firstName={firstName} docs={channelDocs} onClose={() => setChannelsOpen(false)} />
      ) : null}
    </div>
  );
}
